/**
 * Module-level lifecycle manager for Ghostwriter.
 *
 * Two-layer defense against stale user data (modeled after messaging's
 * cache cleanup strategy):
 *
 *   Layer 1 (Primary): Subscribe to `UserDidLogout` via jstBroadcast.
 *     Android-only — the broadcast is not available on iOS.
 *     Set up once via `setupLogoutBroadcast` on first init.
 *
 *   Layer 2 (Fallback): User-ID mismatch on access. Every hook call
 *     compares the current accountId with the persisted one. On
 *     mismatch (logout + different user, app crash, etc.) all
 *     ghostwriter state is cleared and re-initialized.
 *
 * Also gates all ghostwriter functionality behind the
 * `rn_ghostwriter_test` proctor allocation.
 */

import { successAsyncState } from "./AsyncState";
import { getLocalConsent } from "./ghostwriterApi";
import {
  resetStore,
  setAutoAppliableJobKeys,
  setConsentStatusState,
  setGhostwriterAsyncAutoApply,
  setGhostwriterSyncPrefill,
  setGhostwriterDeprecated,
  setGhostwriterHybridAutomation,
  setGhostwriterSyncAutoApply,
  setEligibilityAllowlistJobKeys,
} from "./GhostwriterStore";
import { getAccountStatus } from "../../native-modules/tare/getAccountStatus";
import { localPrefs } from "../../native-modules/tare/getLocalPrefs";
import * as jstBroadcast from "../../native-modules/tare/jstBroadcast";
import { resolveRnGhostwriterAllocation } from "../utils/ghostwriterProctorUtils";

import { Platform } from "react-native";

export const GHOSTWRITER_PREFS_NAMESPACE = "ghostwriter";
const ACCOUNT_ID_KEY = "accountId";

let currentAccountId: string | null = null;
/** The accountId that `initPromise` is resolving for. */
let initAccountId: string | null = null;
let initPromise: Promise<void> | null = null;
let logoutSubscription: jstBroadcast.JstBroadcastSubscription | null = null;
let logoutSubscribing = false;

// ---------------------------------------------------------------------------
// Logout broadcast (Layer 1)
// ---------------------------------------------------------------------------

function setupLogoutBroadcast(): void {
  if (Platform.OS !== "android") return;
  if (logoutSubscription || logoutSubscribing) return;
  logoutSubscribing = true;

  void jstBroadcast
    .subscribe("UserDidLogout", () => {
      void clearGhostwriterData();
    })
    .then((sub) => {
      if (sub) {
        logoutSubscription = sub;
      } else {
        logoutSubscribing = false;
      }
    })
    .catch(() => {
      // iOS: subscription currently fails silently; allow retry on next init.
      logoutSubscribing = false;
    });
}

// ---------------------------------------------------------------------------
// User mismatch detection (Layer 2)
// ---------------------------------------------------------------------------

/**
 * Checks whether the incoming accountId differs from the one we
 * initialized for. On cold start (`currentAccountId` is null) falls
 * back to the value persisted in TareBridge.
 *
 * `localPrefs.getString` returns `''` when no value is stored (some
 * platforms may return `null`). Both are treated as "no previous user."
 */
async function hasUserMismatch(accountId: string): Promise<boolean> {
  if (currentAccountId) {
    return currentAccountId !== accountId;
  }
  const persisted = await localPrefs.getString(GHOSTWRITER_PREFS_NAMESPACE, ACCOUNT_ID_KEY);
  return !!persisted && persisted !== accountId;
}

// ---------------------------------------------------------------------------
// Initialization
// ---------------------------------------------------------------------------

/**
 * Clears persisted and in-memory store data without resetting
 * init bookkeeping (`initAccountId`/`initPromise`). Used by
 * `doInitialize` so an in-flight init can survive its own clear.
 */
async function clearPersistedData(): Promise<void> {
  await localPrefs.clearNamespace(GHOSTWRITER_PREFS_NAMESPACE);
  resetStore();
  currentAccountId = null;
}

async function doInitialize(accountId: string): Promise<void> {
  try {
    if (await hasUserMismatch(accountId)) {
      await clearPersistedData();
    }

    // Resolve all three bucket flags from a single bridge read so they
    // are guaranteed to come from the same allocation snapshot. Splitting
    // this across separate `getProctorAllocation` calls would allow a
    // transient bridge failure (or matrix refresh mid-init) to persist an
    // impossible combination like `isAsyncAutoApply && isDeprecated`.
    const { isAsyncAutoApply, syncPrefill, isHybridAutomation, deprecated, syncAutoApply } =
      await resolveRnGhostwriterAllocation().catch(() => ({
        isAsyncAutoApply: false,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        syncPrefill: { inBucket: false, jobKeys: [] as string[] },
        isHybridAutomation: false,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        deprecated: { inBucket: false, jobKeys: [] as string[] },
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
        syncAutoApply: { inBucket: false, jobKeys: [] as string[] },
      }));

    currentAccountId = accountId;
    await localPrefs.setString(GHOSTWRITER_PREFS_NAMESPACE, ACCOUNT_ID_KEY, accountId);

    if (initAccountId !== accountId) return;

    // sync_prefill_submit/deprecated/sync_auto_apply fast-path only when their payload delivered
    // jobKeys; an empty/absent payload (e.g. debug-force) falls back to the
    // `async_auto_apply_submit` flow. For sync_prefill_submit and sync_auto_apply the payload is a traffic-cap
    // allowlist, NOT eligibility — ViewJob runs a real-time per-job read for
    // allowlisted jobs; only deprecated's payload seeds eligibility directly.
    const syncPrefillFastPath = syncPrefill.inBucket && syncPrefill.jobKeys.length > 0;
    const syncPrefillFallback = syncPrefill.inBucket && !syncPrefillFastPath;
    const deprecatedFastPath = deprecated.inBucket && deprecated.jobKeys.length > 0;
    const deprecatedFallback = deprecated.inBucket && !deprecatedFastPath;
    const syncAutoApplyFastPath = syncAutoApply.inBucket && syncAutoApply.jobKeys.length > 0;
    const syncAutoApplyFallback = syncAutoApply.inBucket && !syncAutoApplyFastPath;

    setGhostwriterAsyncAutoApply(
      isAsyncAutoApply || syncPrefillFallback || deprecatedFallback || syncAutoApplyFallback,
    );
    setGhostwriterSyncPrefill(syncPrefillFastPath);
    setGhostwriterDeprecated(deprecatedFastPath);
    setGhostwriterHybridAutomation(isHybridAutomation);
    // Seed the allowlist from whichever fast-path bucket is active (only one ever
    // is) before the sync_auto_apply flag, so ViewJob never sees a bucket flag with an
    // empty candidate set.
    setEligibilityAllowlistJobKeys(
      new Set(
        syncPrefillFastPath
          ? syncPrefill.jobKeys
          : syncAutoApplyFastPath
            ? syncAutoApply.jobKeys
            : [],
      ),
    );
    setGhostwriterSyncAutoApply(syncAutoApplyFastPath);

    // Eligibility seeding is deprecated-only: sync_prefill_submit and sync_auto_apply read eligibility
    // live on ViewJob (allowlist above), not from the payload.
    if (deprecatedFastPath) {
      setAutoAppliableJobKeys(successAsyncState(new Set(deprecated.jobKeys)));
    }
    // Pre-hydrate positive local consent for sync_prefill_submit, deprecated AND sync_auto_apply so the ViewJob
    // CTA reads "Auto apply" immediately instead of flashing "Try auto apply".
    if (syncPrefillFastPath || deprecatedFastPath || syncAutoApplyFastPath) {
      const localConsent = await getLocalConsent();
      if (initAccountId !== accountId) return;
      if (localConsent === true) {
        setConsentStatusState(successAsyncState({ hasConsented: true }));
      }
    }
  } catch {
    if (initAccountId === accountId) {
      initPromise = null;
      initAccountId = null;
      setGhostwriterAsyncAutoApply(false);
      setGhostwriterSyncPrefill(false);
      setGhostwriterDeprecated(false);
      setGhostwriterHybridAutomation(false);
      setGhostwriterSyncAutoApply(false);
      setEligibilityAllowlistJobKeys(new Set());
    }
  }
}

/**
 * Ensures ghostwriter is initialized for the given account. On first
 * call or user mismatch, clears stale data and checks proctor.
 * Subsequent calls with the same accountId are no-ops.
 *
 * `initAccountId` and `initPromise` are claimed synchronously before
 * any async work to prevent concurrent calls from racing.
 */
export async function ensureGhostwriterInitialized(accountId: string | null): Promise<void> {
  if (!accountId) {
    initAccountId = null;
    initPromise = null;
    setGhostwriterAsyncAutoApply(false);
    setGhostwriterSyncPrefill(false);
    setGhostwriterDeprecated(false);
    setGhostwriterHybridAutomation(false);
    setGhostwriterSyncAutoApply(false);
    setEligibilityAllowlistJobKeys(new Set());
    return;
  }

  setupLogoutBroadcast();

  if (initPromise && initAccountId === accountId) {
    return initPromise;
  }

  initAccountId = accountId;
  initPromise = doInitialize(accountId);
  return initPromise;
}

/**
 * Clears all ghostwriter persisted and in-memory state.
 * Called by the `UserDidLogout` broadcast handler (Layer 1) and
 * available for external callers that need a full reset.
 */
export async function clearGhostwriterData(): Promise<void> {
  await clearPersistedData();
  initAccountId = null;
  initPromise = null;
}

// ---------------------------------------------------------------------------
// Android account-status wait
// ---------------------------------------------------------------------------

const ACCOUNT_STATUS_TIMEOUT_MS = 10_000;

/**
 * On Android, `accountStatus.accountId` may not be populated at the
 * time React Native mounts (the native model hasn't finished
 * hydrating). This mirrors the same workaround used by the messaging
 * module: subscribe to the `DataUpdated` broadcast for
 * `TARE.JSTAccountStatus`, re-check after subscribing (race-condition
 * guard), and time out after 10 s to avoid blocking indefinitely when
 * the user is logged out.
 */
export function waitForAccountId(): Promise<string | null> {
  return new Promise((resolve) => {
    let subscription: jstBroadcast.JstBroadcastSubscription | null = null;
    let resolved = false;

    const resolveAndCleanup = (accountId: string | null) => {
      if (resolved) return;
      resolved = true;
      void subscription?.remove();
      resolve(accountId);
    };

    void jstBroadcast
      .subscribe("DataUpdated", (event) => {
        if (event.name === "TARE.JSTAccountStatus") {
          void getAccountStatus().then((status) => {
            resolveAndCleanup(status?.accountId ?? null);
          });
        }
      })
      .then((sub) => {
        subscription = sub;
        void getAccountStatus().then((status) => {
          if (status?.accountId) {
            resolveAndCleanup(status.accountId);
          }
        });
      });

    setTimeout(() => resolveAndCleanup(null), ACCOUNT_STATUS_TIMEOUT_MS);
  });
}

/**
 * Resets all module-level state without clearing persisted storage.
 * Intended for tests to ensure isolation between test cases.
 */
export function resetLifecycleState(): void {
  currentAccountId = null;
  initAccountId = null;
  initPromise = null;
  void logoutSubscription?.remove();
  logoutSubscription = null;
  logoutSubscribing = false;
}
